<template>
  <VerificationForm :skip-button="loaded && !phoneVerifide" :loader="!loaded" @skip-click="onSkipButtonClick">
    <template #title>
      <div class="phone-verification-body__label">{{ title }}</div>
    </template>
    <template #description>
      <template v-if="!code_sended"> {{ $t('emailSignedTitle') }}<br />{{ $t('emailSignedDescription') }} </template>
      <template v-if="code_sended">
        {{ $t('confirmPhoneDescription') }}
        <span class="phone-verification-description__phone-number">{{ phone }}</span>
      </template>
    </template>
    <template #input>
      <ValidationField :errors="phone_errors" v-if="!code_sended">
        <SInput
          v-model="phone"
          :label="$t('phoneNumberLabel')"
          :class="{
            'invalid--element-border': phone_errors.length > 0,
          }"
          :unmasked="false"
          :mask="['+7#{+}']"
        ></SInput>
      </ValidationField>
      <ValidationField :errors="code_errors" v-if="code_sended">
        <SInput
          v-model="code"
          :label="$t('codeInputLabel')"
          :class="{
            'invalid--element-border': code_errors.length > 0,
          }"
          :mask="['######']"
        ></SInput>
      </ValidationField>
    </template>
    <template #actions>
      <SButton v-if="!code_sended" :disabled="send_code_button_disabled" wide @click="onSendCodeClick">{{
        $t('getCodeButtonLabel')
      }}</SButton>
      <template v-else>
        <SButton :disabled="accept_button_disabled" wide @click="onAcceptCodeClick">{{
          $t('sendCodeButtonLabel')
        }}</SButton>
        <div v-if="!timese_out" class="phone-verification-actions__timer">
          {{ $t('resendTimerLabel') }} {{ timer_string }}
        </div>
        <SButton v-else wide variant="tertiary" @click="onResendCodeClick">{{ $t('resendCodeButtonLabel') }}</SButton>
      </template>
    </template>
  </VerificationForm>
</template>

<script setup lang="ts">
import { SInput, SButton } from '@synergy/ui-kit-vue3'
import { computed, ref, toRefs, watch } from 'vue'
import { useTranslate } from '@tolgee/vue'
import { useRouter } from 'vue-router'

// Components
import ValidationField from '@/components/ValidationField/ValidationField.vue'
import VerificationForm from '../VerrificationForm/VerificationForm.vue'

import { verifyPhoneByCode, sendCode, setAuthForm } from '@/api/api'

const emit = defineEmits(['skip-click'])

const props = defineProps({
  token: {
    type: String,
    default: '',
  },
  redirectUri: {
    type: String,
    default: '',
  },
})

const { token, redirectUri } = toRefs(props)
const { t } = useTranslate()
const router = useRouter()

const loaded = ref(true)
const phone = ref('')
const code = ref('')
const code_sended = ref(false)
const timer_string = ref('00:00')
const timese_out = ref(false)
const send_code_timer = ref<number | null>(null)
const uuid = ref('')
const code_errors = ref<string[]>([])
const phone_errors = ref<string[]>([])
const phoneVerifide = ref(false)
const code_expire = ref(false)
const error_state = ref(false)

const accept_button_disabled = computed(() => {
  return !code.value || code_expire.value || error_state.value
})

const send_code_button_disabled = computed(() => {
  return !phone.value || phone_errors.value.length > 0
})

const title = computed(() => {
  if (!code_sended.value) {
    return t.value('addPhoneNumberTitle')
  } else {
    return t.value('confirmPhoneNumberTitle')
  }
})

function startCodeTimer(expires: number) {
  if (send_code_timer.value) {
    clearInterval(send_code_timer.value)
  }

  send_code_timer.value = setInterval(function () {
    if (expires > 0) {
      expires = expires - 1
      const minutes = Math.floor(expires / 60)
      const seconds = expires - minutes * 60
      timer_string.value = formatTimeString(minutes, '0', 2) + ':' + formatTimeString(seconds, '0', 2)
    } else {
      send_code_timer.value && clearInterval(send_code_timer.value)
      timer_string.value = '00:00'
      timese_out.value = true
    }
  }, 1000)
}

function formatTimeString(timeString: number, pad: string, length: number) {
  return (new Array(length + 1).join(pad) + timeString).slice(-length)
}

function onSkipButtonClick() {
  emit('skip-click')
}

function onResendCodeClick() {
  sendCode(phone.value, token.value)
    .then((data: any) => {
      timese_out.value = false
      uuid.value = data?.id || ''
      code_sended.value = true
      code_expire.value = false
      code_errors.value = []
      startCodeTimer(data.seconds_to_unblock)
    })
    .catch(() => {
      //
    })
}

function onSendCodeClick() {
  sendCode(phone.value, token.value)
    .then((resp: any) => {
      timese_out.value = false
      uuid.value = resp.id || null
      code_sended.value = true
      startCodeTimer(resp.seconds_to_unblock)
    })
    .catch((err) => {
      phone_errors.value = []
      if (err.message == 'Номер телефона введен неверно') {
        phone_errors.value = [t.value('incorrectPhoneError')]
      }
    })
}

function onAcceptCodeClick() {
  loaded.value = false
  verifyPhoneByCode(uuid.value, code.value, token.value)
    .then(() => {
      setAuthForm(token.value).then(() => {
        loaded.value = true
        router.push({ name: 'success_verification', query: { type: 'phone', redirectUri: redirectUri.value } })
      })
    })
    .catch((err: any) => {
      console.log('ERR')
      code_errors.value = []
      loaded.value = true

      if (err.code == 422) {
        if (err.message == 'Код устарел. Отправьте новый и попробуйте снова.') {
          code_errors.value = [t.value('expireCodeError')]
          code_expire.value = true
        } else if (err.message == 'Неверный код') {
          error_state.value = true
          code_errors.value = [t.value('incorrectCodeError')]
        } else if (err.message == 'Номер телефона введен неверно') {
          error_state.value = true
          code_errors.value = [t.value('incorrectPhoneError')]
        } else {
          code_errors.value = [err?.message || '']
        }
      } else {
        router.push({ name: 'phone_confirmation', params: { code: err.code }, query: { token: token.value } })
      }
    })
}

watch(code, () => {
  if (code_errors.value.length > 0) {
    code_errors.value = []
    error_state.value = false
  }
})

watch(phone, () => {
  if (phone_errors.value.length > 0) {
    phone_errors.value = []
  }
})
</script>

<style scoped lang="scss">
.phone-verification {
  display: flex;
  flex-direction: column;
  gap: 36px;
  max-width: 540px;
  width: 100%;
  border-radius: 4px;
  padding: 40px;
  overflow: hidden;
  box-sizing: border-box;

  box-shadow: 0px 6px 18px 0px #00000017;
}

.phone-verification-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__logo {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: auto;
    height: 48px;
  }
}

.phone-verification-body {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__label {
    font-weight: 500;
    font-size: 28px;
    line-height: 36px;
    letter-spacing: 0%;
    text-align: center;
  }
}

.phone-verification-description {
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0%;
  text-align: center;

  &__phone-number {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0%;
    text-align: center;
  }
}

.phone-verification-actions {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;

  &__timer {
    white-space: nowrap;
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0%;
    text-align: center;
    vertical-align: middle;
    color: var(--color-text-secondary);
  }
}

.verification-preloader {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 246px;
}

.skip-button {
  display: flex;
  background-color: transparent;
  padding: 0;
  margin: 0;
  border: none;
  color: #ed131c;
  cursor: pointer;

  &__label {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-weight: 500;
    font-size: 18px;
    line-height: 18px;
    letter-spacing: 0%;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
  }
}
</style>
